'use client';

import { useActiveHighlightSection } from '@/app/(consent-manger)/[locale]/(lagacy-footer)/document/[...segments]/hooks/useActiveHighlightSection';
import { HoverPreviewManager } from '@/elements/html-viewer/HoverPreviewManager';
import { useActiveHighlight } from '@/hooks/useActiveHighlight';
import {
  buildLeafHoverIds,
  findLeafHoverTarget,
  hasTextSelection,
  readMenuOffset,
} from '@/lib/utils/leafHoverTargets';
import { useLocale, useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ElementOptionsMenu } from './element-options-menu';
import styles from './html-link-handler.module.css';
import { LinkOptionsMenu } from './link-options-menu';

interface HtmlLinkHandlerProps {
  htmlContent: string;
  legislationId?: number | undefined;
  documentId?: number | undefined;
  iri?: string | undefined;
  className?: string | undefined;
  isHighlighted?: boolean | undefined;
}

/**
 * Moves the hover outline onto `target`, clearing it from the previously
 * hovered element — unless that one is actively highlighted, whose outline is
 * owned by the highlight system rather than by hover.
 *
 * Deliberately NOT shared with html-viewer: that viewer removes the outline
 * unconditionally, having no active-highlight exemption.
 */
function moveHoverOutline(
  previous: HTMLElement | null,
  target: HTMLElement,
  hoverOutlineClass: string,
  activeHighlightClass: string
): void {
  if (
    previous &&
    previous !== target &&
    !previous.classList.contains(activeHighlightClass)
  ) {
    previous.classList.remove(hoverOutlineClass);
  }
  target.classList.add(hoverOutlineClass);
}

/** Everything the container-level hover/click handlers need from the component. */
interface HoverHandlerContext {
  container: HTMLElement;
  activeHighlightClass: string;
  hoverOutlineClass: string;
  isHoveringMenuAreaRef: { current: boolean };
  currentHoverRef: { current: HTMLElement | null };
  hoveredElementIdRef: { current: string | null };
  activeElementIdRef: { current: string | null };
  menuHideTimeoutRef: { current: ReturnType<typeof setTimeout> | null };
  menuWrapperRef: { current: HTMLDivElement | null };
  findHoverTarget: (el: HTMLElement) => HTMLElement | null;
  applyBadgeHighlights: (id: string, container: HTMLElement) => void;
  startGracePeriod: () => void;
  toggleActiveHighlight: (id: string) => void;
}

/**
 * Container-level `mouseover`: moves the hover outline and the floating menu
 * onto the leaf section under the pointer.
 *
 * Each handler is a module-scope function taking its context explicitly, so
 * none of them nests inside the effect — nesting is what made this logic hard
 * to follow (and to lint) when it lived inline.
 */
function handleHoverMouseOver(e: MouseEvent, ctx: HoverHandlerContext): void {
  if ((e.target as HTMLElement).closest('a')) return;
  if (ctx.isHoveringMenuAreaRef.current) return;

  const target = ctx.findHoverTarget(e.target as HTMLElement);
  if (!target) return;
  if (target === ctx.currentHoverRef.current) return;
  if (target.classList.contains(ctx.activeHighlightClass)) return;

  // Cancel any pending hide timeout
  if (ctx.menuHideTimeoutRef.current) {
    clearTimeout(ctx.menuHideTimeoutRef.current);
    ctx.menuHideTimeoutRef.current = null;
  }

  // READ positions BEFORE any class changes — avoids forced reflow
  const wrapper = ctx.menuWrapperRef.current;
  const offset = wrapper ? readMenuOffset(ctx.container, target) : null;

  // WRITE phase — all DOM mutations batched together
  moveHoverOutline(
    ctx.currentHoverRef.current,
    target,
    ctx.hoverOutlineClass,
    ctx.activeHighlightClass
  );
  ctx.currentHoverRef.current = target;

  if (wrapper && offset) {
    wrapper.style.transform = `translate(${offset.left}px, ${offset.top}px)`;
    wrapper.style.visibility = 'visible';
  }

  ctx.activeElementIdRef.current = target.id;
  ctx.hoveredElementIdRef.current = target.id;
  ctx.applyBadgeHighlights(target.id, ctx.container);
}

/**
 * Container-level `mouseout`: starts the grace period when leaving the section
 * that owns the menu, otherwise just drops that section's hover outline.
 */
function handleHoverMouseOut(e: MouseEvent, ctx: HoverHandlerContext): void {
  if (ctx.isHoveringMenuAreaRef.current) return;

  const target = ctx.findHoverTarget(e.target as HTMLElement);
  if (!target) return;

  // Moving within the same section is not a leave.
  const related = e.relatedTarget as HTMLElement | null;
  if (related && target.contains(related)) return;

  if (target.id === ctx.hoveredElementIdRef.current) {
    ctx.startGracePeriod();
    return;
  }
  // An actively-highlighted section keeps its outline; hover does not own it.
  if (target.classList.contains(ctx.activeHighlightClass)) return;

  target.classList.remove(ctx.hoverOutlineClass);
  if (ctx.currentHoverRef.current === target) {
    ctx.currentHoverRef.current = null;
  }
}

/** Container-level click: toggles the highlight on the clicked leaf section. */
function handleHoverClick(e: MouseEvent, ctx: HoverHandlerContext): void {
  const target = e.target as HTMLElement;

  // Don't trigger when user is selecting text
  if (hasTextSelection()) return;

  // Don't trigger on links or menu buttons
  if (target.closest('a')) return;
  if (target.closest('[role="button"]') || target.closest('button')) return;

  // Reuse findHoverTarget for click (same leaf-only logic)
  const elementWithId = ctx.findHoverTarget(target);
  if (!elementWithId) return;

  e.stopPropagation();
  ctx.toggleActiveHighlight(elementWithId.id);
}

/** Binds the three container-level handlers to one context. */
function createHoverHandlers(ctx: HoverHandlerContext) {
  return {
    handleMouseOver: (e: MouseEvent) => handleHoverMouseOver(e, ctx),
    handleMouseOut: (e: MouseEvent) => handleHoverMouseOut(e, ctx),
    handleNativeClick: (e: MouseEvent) => handleHoverClick(e, ctx),
  };
}

function HtmlLinkHandler({
  htmlContent,
  legislationId,
  documentId,
  iri,
  className = '',
  isHighlighted = true,
}: HtmlLinkHandlerProps) {
  // State for link options menu (React state OK — only changes on click)
  const [linkOptionsVisible, setLinkOptionsVisible] = useState(false);
  const [currentHref, setCurrentHref] = useState<string | null>(null);
  const [linkPosition, setLinkPosition] = useState({
    x: 0,
    y: 0,
    placeAbove: false,
  });

  // Refs and constants
  const contentRef = useRef<HTMLDivElement>(null);
  const menuHeight = 150;

  // Hover system — refs only (no React state) for zero-lag hover feedback.
  const activeElementIdRef = useRef<string | null>(null);
  const hoveredElementIdRef = useRef<string | null>(null);
  const isHoveringMenuAreaRef = useRef(false);
  const currentHoverRef = useRef<HTMLElement | null>(null);
  const menuHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuWrapperRef = useRef<HTMLDivElement>(null);

  const tCommon = useTranslations('Common');
  const locale = useLocale();

  // CSS class names from module (with fallbacks for type safety)
  const activeHighlightClass = styles['activeHighlight'] ?? 'activeHighlight';
  const hoverOutlineClass = styles['hoverOutline'] ?? 'hoverOutline';

  // Use the unified active highlight system
  const {
    highlights,
    addActiveHighlight,
    removeActiveHighlight,
    toggleActiveHighlight,
  } = useActiveHighlight(activeHighlightClass);

  const { highlightAndScrollToSection } =
    useActiveHighlightSection(addActiveHighlight);

  // Re-apply active highlights when DOM is mutated (dangerouslySetInnerHTML wipes classes)
  // Using MutationObserver to detect actual DOM changes instead of re-rendering on every state change
  useEffect(() => {
    if (!contentRef.current) return;

    const observer = new MutationObserver(() => {
      // Re-apply all active highlights after DOM mutation
      highlights.forEach((highlight) => {
        const element = document.getElementById(highlight.elementId);
        if (element && !element.classList.contains(activeHighlightClass)) {
          element.classList.remove(hoverOutlineClass);
          element.classList.add(activeHighlightClass);
        }
      });
    });

    observer.observe(contentRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [highlights, activeHighlightClass, hoverOutlineClass]);

  // Ref for badge highlight cleanup
  const highlightedBadgesRef = useRef<HTMLElement[]>([]);

  // Imperatively highlight/clear sidebar badges for the hovered section.
  // Called from handleMouseOver and hideHoverUI (no React state involved).
  const clearBadgeHighlights = useCallback(() => {
    for (const badge of highlightedBadgesRef.current) {
      badge.style.removeProperty('outline');
      badge.style.removeProperty('outline-offset');
      badge.style.removeProperty('border-radius');
    }
    highlightedBadgesRef.current = [];
  }, []);

  const applyBadgeHighlights = useCallback(
    (targetId: string, container: HTMLElement) => {
      clearBadgeHighlights();

      const legislationContent = container.closest('.legislation-content');
      if (!legislationContent) return;

      const badge = legislationContent.querySelector<HTMLElement>(
        `[data-section-id="${CSS.escape(targetId)}"]`
      );
      if (badge) {
        badge.style.outline = '2px solid var(--highlight-border)';
        badge.style.outlineOffset = '2px';
        badge.style.borderRadius = '6px';
        highlightedBadgesRef.current.push(badge);
      }
    },
    [clearBadgeHighlights]
  );
  // Shared helper: hide both outline and menu button atomically.
  const hideHoverUI = useCallback(() => {
    const el = hoveredElementIdRef.current
      ? document.getElementById(hoveredElementIdRef.current)
      : currentHoverRef.current;
    if (el) el.classList.remove(hoverOutlineClass);
    currentHoverRef.current = null;
    hoveredElementIdRef.current = null;
    activeElementIdRef.current = null;
    if (menuWrapperRef.current)
      menuWrapperRef.current.style.visibility = 'hidden';
    clearBadgeHighlights();
  }, [hoverOutlineClass, clearBadgeHighlights]);

  // Shared helper: start grace period (100ms) before hiding.
  const startGracePeriod = useCallback(() => {
    if (menuHideTimeoutRef.current) {
      clearTimeout(menuHideTimeoutRef.current);
    }
    menuHideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMenuAreaRef.current) {
        hideHoverUI();
      }
    }, 100);
  }, [hideHoverUI]);

  // Getter for active element ID — read from ref at click time (avoids re-renders)
  const getActiveElementId = useCallback(
    () => activeElementIdRef.current ?? '',
    []
  );

  // Setup native event listeners for hover and click
  useEffect(() => {
    const container = contentRef.current;
    if (!container || !isHighlighted) return;

    // The leaf index, built at most once. `null` means "not built yet" — see
    // `ensureLeafIds` below for why that is a state worth having.
    let leafIds: Set<string> | null = null;

    /**
     * The index, built on whichever comes first: browser idle time, or the
     * first interaction that actually needs it.
     *
     * Building it walks every id-bearing node in the statute (`[id]` across a
     * body the SSR helper measures at ~458k characters), then re-walks each
     * one's ancestor chain to derive the parent→children map. That is the most
     * expensive thing this component does, and it used to run inline in the
     * effect — i.e. inside the commit that follows hydration, on the main
     * thread, in front of whatever the user tapped first. Mobile INP is the
     * metric that measures exactly that wait, and it is the one Search Console
     * reports as failing on this route.
     *
     * Deferring it to `requestIdleCallback` is only safe because nothing
     * PAINTS from it: the index feeds hover and click target resolution, not
     * the rendered statute. But an interaction can still land in the window
     * before idle fires, so the listeners below go on immediately and call
     * this — which builds synchronously if idle has not run yet. Worst case is
     * therefore what shipped before; the common case is free.
     */
    const ensureLeafIds = (): Set<string> => {
      leafIds ??= buildLeafHoverIds(container);
      return leafIds;
    };

    // Lazy cache: repeated hovers on the same DOM element are O(1).
    const hoverTargetCache = new WeakMap<HTMLElement, HTMLElement | null>();

    // Nearest leaf-level hover target; index resolved lazily on cache miss.
    const findHoverTarget = (el: HTMLElement): HTMLElement | null =>
      findLeafHoverTarget(el, container, ensureLeafIds, hoverTargetCache);

    const { handleMouseOver, handleMouseOut, handleNativeClick } =
      createHoverHandlers({
        container,
        activeHighlightClass,
        hoverOutlineClass,
        isHoveringMenuAreaRef,
        currentHoverRef,
        hoveredElementIdRef,
        activeElementIdRef,
        menuHideTimeoutRef,
        menuWrapperRef,
        findHoverTarget,
        applyBadgeHighlights,
        startGracePeriod,
        toggleActiveHighlight,
      });

    // Listeners first, index second. Attaching is cheap and it is what makes
    // the deferral above safe: an interaction arriving before idle still
    // resolves, it just pays for the index itself.
    container.addEventListener('mouseover', handleMouseOver, { passive: true });
    container.addEventListener('mouseout', handleMouseOut, { passive: true });
    container.addEventListener('click', handleNativeClick);

    // Safari still ships no requestIdleCallback; a macrotask is the portable
    // fallback — it yields to any pending input before running.
    const idle = window.requestIdleCallback;
    const idleHandle =
      typeof idle === 'function'
        ? idle(() => ensureLeafIds(), { timeout: 2_000 })
        : null;
    const timeoutHandle =
      idleHandle === null
        ? window.setTimeout(() => ensureLeafIds(), 200)
        : null;

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      container.removeEventListener('click', handleNativeClick);
      if (idleHandle !== null) window.cancelIdleCallback(idleHandle);
      if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
      if (menuHideTimeoutRef.current) {
        clearTimeout(menuHideTimeoutRef.current);
        menuHideTimeoutRef.current = null;
      }
    };
  }, [
    isHighlighted,
    hoverOutlineClass,
    toggleActiveHighlight,
    startGracePeriod,
  ]);

  // Improved link click handler
  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');

    if (link) {
      e.preventDefault();
      e.stopPropagation();

      const href = link.getAttribute('href') || '';
      const rect = link.getBoundingClientRect();

      const containerRect = contentRef.current?.getBoundingClientRect();

      if (!containerRect) return;

      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const placeAbove = spaceBelow < menuHeight;

      setCurrentHref(href);
      setLinkPosition({
        x: Math.max(0, rect.left - containerRect.left),
        y: placeAbove
          ? rect.top - containerRect.top
          : rect.bottom - containerRect.top,
        placeAbove,
      });

      setLinkOptionsVisible(true);
    }
  };

  // Highlight and scroll to section when hash changes
  useEffect(() => {
    const setupHighlightAndScroll = () => {
      const hash = window.location.hash.substring(1);

      if (hash && contentRef.current) {
        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            highlightAndScrollToSection(hash);
          });
        });
      }
    };

    const handleHashChange = () => {
      setupHighlightAndScroll();
    };

    if (htmlContent) {
      setupHighlightAndScroll();
    }

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [htmlContent, highlightAndScrollToSection]);

  // Handle link option selection
  const handleOptionSelected = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setLinkOptionsVisible(false);
  };

  /**
   * One layout read per highlight, shared by both overlays below.
   *
   * `renderCloseButtons` and `renderHighlightMenus` each used to call
   * `getBoundingClientRect` on the highlighted element AND on the container,
   * so a click that toggles a highlight forced four synchronous layouts per
   * highlight during render — on the click's own critical path, which is
   * precisely the interval INP measures. The reads are unavoidable (both
   * overlays are absolutely positioned from real geometry) but they are the
   * same reads, so taking them once halves the cost and, more importantly,
   * groups them into a single read phase instead of interleaving with React's
   * element creation.
   */
  const highlightGeometry = (() => {
    const container = contentRef.current;
    if (!container) return [];
    const containerRect = container.getBoundingClientRect();
    return Array.from(highlights.values()).flatMap((highlight) => {
      const element = document.getElementById(highlight.elementId);
      if (!element) return [];
      return [
        {
          elementId: highlight.elementId,
          rect: element.getBoundingClientRect(),
          containerRect,
        },
      ];
    });
  })();

  // Render close buttons for active highlights
  const renderCloseButtons = () => {
    return highlightGeometry.map(({ elementId, rect, containerRect }) => {
      return (
        <button
          key={elementId}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            removeActiveHighlight(elementId);
          }}
          className='absolute z-50 bg-transparent rounded-full w-7 h-7 flex items-center justify-center hover:bg-black/5 transition-colors duration-150'
          style={{
            top: `${rect.top - containerRect.top - 4}px`,
            left: `${rect.right - containerRect.left - 4}px`,
          }}
          aria-label={tCommon('removeHighlight')}
          title={tCommon('removeHighlight')}
          type='button'
        >
          <span className='text-content-muted text-xl font-light leading-none hover:text-content-secondary'>
            ×
          </span>
        </button>
      );
    });
  };

  // Render 3 dots menu for active highlights (yellow box)
  const renderHighlightMenus = () => {
    return highlightGeometry.map(({ elementId, rect, containerRect }) => {
      return (
        <div
          key={`menu-${elementId}`}
          style={{
            position: 'absolute',
            left: `${rect.left - containerRect.left - 50}px`,
            top: `${rect.top - containerRect.top}px`,
            pointerEvents: 'auto',
          }}
        >
          <ElementOptionsMenu
            elementId={elementId}
            activeElementPosition={{
              x: 50,
              y: 0,
              width: rect.width,
              height: rect.height,
            }}
            iri={iri}
            documentId={documentId || legislationId}
          />
        </div>
      );
    });
  };

  return (
    <div className={`relative ${className} slov-lex-style`}>
      <div
        ref={contentRef}
        className={styles['contentContainer']}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        onClick={handleLinkClick}
      />

      {/* Element options menu — always mounted, visibility toggled via DOM */}
      <div
        ref={menuWrapperRef}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          willChange: 'transform',
        }}
      >
        <div
          style={{
            pointerEvents: 'auto',
            width: '16px',
            height: '24px',
          }}
          onMouseEnter={() => {
            if (menuHideTimeoutRef.current) {
              clearTimeout(menuHideTimeoutRef.current);
              menuHideTimeoutRef.current = null;
            }
            isHoveringMenuAreaRef.current = true;
            const elId = activeElementIdRef.current;
            if (elId) {
              const element = document.getElementById(elId);
              if (element && !element.classList.contains(hoverOutlineClass)) {
                element.classList.add(hoverOutlineClass);
              }
            }
          }}
          onMouseLeave={() => {
            isHoveringMenuAreaRef.current = false;
            startGracePeriod();
          }}
        >
          <ElementOptionsMenu
            getElementId={getActiveElementId}
            activeElementPosition={{ x: 0, y: 0, width: 0, height: 0 }}
            iri={iri}
            documentId={documentId || legislationId}
          />
        </div>
      </div>

      <HoverPreviewManager
        containerRef={contentRef}
        locale={locale}
        enabled={!linkOptionsVisible}
      />

      <LinkOptionsMenu
        href={currentHref || ''}
        position={linkPosition}
        visible={linkOptionsVisible}
        onClose={() => setLinkOptionsVisible(false)}
        onOptionSelected={handleOptionSelected}
        iri={iri}
      />

      {/* Close buttons for active highlights */}
      {renderCloseButtons()}

      {/* 3 dots menu for active highlights (yellow box) */}
      {renderHighlightMenus()}
    </div>
  );
}

export default memo(HtmlLinkHandler);
